import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import NavBarComponent from "../../navBar/NavBarComponent";
import FooterComponent from "../../home/FooterComponent";
import ModernPricingView from "../../pricing/modernPricingView";
import { sendSlackMessageTracked } from "../../../models/notifications";

// ─── Icons ────────────────────────────────────────────────────────────────────

function BoxIcon() {
    return (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
            <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
            <line x1="12" y1="22.08" x2="12" y2="12"/>
        </svg>
    );
}

function TagIcon() {
    return (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
            <line x1="7" y1="7" x2="7.01" y2="7"/>
        </svg>
    );
}

function CreditIcon() {
    return (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
            <line x1="1" y1="10" x2="23" y2="10"/>
        </svg>
    );
}

function RefreshIcon() {
    return (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 4 23 10 17 10"/>
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
        </svg>
    );
}

function TrendIcon() {
    return (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
            <polyline points="17 6 23 6 23 12"/>
        </svg>
    );
}

function AlertIcon() {
    return (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
    );
}

function CheckIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: "2px" }}>
            <polyline points="20 6 9 17 4 12"/>
        </svg>
    );
}

function XIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: "2px" }}>
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
    );
}

// ─── Sections ─────────────────────────────────────────────────────────────────

function Hero({ onCTA }) {
    return (
        <section style={{ background: "#050505", padding: "100px 24px 80px", textAlign: "center" }}>
            <div style={{ maxWidth: "720px", margin: "0 auto" }}>
                <p style={{ fontSize: "11px", fontWeight: 700, color: "#22c55e", textTransform: "uppercase", letterSpacing: "0.14em", marginBottom: "18px" }}>
                    Inventory Liquidation
                </p>
                <h1 style={{ fontSize: "clamp(30px, 5vw, 52px)", fontWeight: 800, color: "#fff", lineHeight: 1.1, marginBottom: "22px" }}>
                    Move old inventory without burning your margins
                </h1>
                <p style={{ fontSize: "17px", color: "#9ca3af", lineHeight: 1.65, maxWidth: "580px", margin: "0 auto 36px" }}>
                    Deep discounts clear stock but train customers to expect lower prices and cost you twice: once on the margin, again when they never come back. Store credit bonuses move the same inventory and pull that customer in for another order.
                </p>
                <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
                    <button
                        onClick={onCTA}
                        style={{ padding: "14px 28px", background: "#22c55e", color: "#fff", border: "none", borderRadius: "10px", fontSize: "14px", fontWeight: 700, cursor: "pointer" }}
                    >
                        Start free trial
                    </button>
                    <a
                        href="/pricing"
                        style={{ padding: "14px 28px", background: "transparent", color: "#e5e7eb", border: "1px solid #2a2a2a", borderRadius: "10px", fontSize: "14px", fontWeight: 600, textDecoration: "none" }}
                    >
                        See pricing
                    </a>
                </div>
            </div>
        </section>
    );
}

function StatsStrip() {
    const stats = [
        { value: "30%", label: "of Shopify store inventory sits unsold past 90 days" },
        { value: "2x", label: "higher repeat purchase rate when liquidation uses store credit vs markdown" },
        { value: "20%", label: "average increase in order value when customers return to spend credit" },
        { value: "$0", label: "in additional customer acquisition cost for a credit-driven return visit" },
    ];
    return (
        <section style={{ background: "#111", borderTop: "1px solid #1a1a1a", borderBottom: "1px solid #1a1a1a", padding: "40px 24px" }}>
            <div style={{ maxWidth: "960px", margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "32px", textAlign: "center" }}>
                {stats.map((s) => (
                    <div key={s.label}>
                        <div style={{ fontSize: "28px", fontWeight: 800, color: "#22c55e", marginBottom: "6px" }}>{s.value}</div>
                        <div style={{ fontSize: "12.5px", color: "#6b7280", lineHeight: 1.5 }}>{s.label}</div>
                    </div>
                ))}
            </div>
        </section>
    );
}

function ProblemSection() {
    const problems = [
        {
            icon: <BoxIcon />,
            title: "Dead stock ties up cash",
            body: "Inventory sitting in a warehouse is cash you can't use. Every week it sits, storage costs compound and the product's resale value drops.",
        },
        {
            icon: <TagIcon />,
            title: "Deep discounts have side effects",
            body: "A 40% off sale moves product, but it also signals to your customer base that your prices are negotiable. The next time they shop at full price, they'll hesitate.",
        },
        {
            icon: <AlertIcon />,
            title: "Third-party liquidators destroy the brand",
            body: "Selling overstock to liquidators gets your product into discount channels and outlet stores. Customers see it there and question why they paid full price.",
        },
    ];
    return (
        <section style={{ background: "#0a0a0a", padding: "80px 24px" }}>
            <div style={{ maxWidth: "900px", margin: "0 auto" }}>
                <div style={{ textAlign: "center", marginBottom: "56px" }}>
                    <p style={{ fontSize: "11px", fontWeight: 700, color: "#22c55e", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: "10px" }}>
                        The Problem
                    </p>
                    <h2 style={{ fontSize: "clamp(24px, 4vw, 36px)", fontWeight: 800, color: "#fff", lineHeight: 1.2 }}>
                        Standard liquidation trades one problem for another
                    </h2>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "20px" }}>
                    {problems.map((p) => (
                        <div key={p.title} style={{ background: "#111", border: "1px solid #1e1e1e", borderRadius: "16px", padding: "28px" }}>
                            <div style={{ marginBottom: "16px" }}>{p.icon}</div>
                            <h3 style={{ fontSize: "16px", fontWeight: 700, color: "#fff", marginBottom: "10px" }}>{p.title}</h3>
                            <p style={{ fontSize: "13.5px", color: "#9ca3af", lineHeight: 1.65 }}>{p.body}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function HowItWorksSection() {
    const steps = [
        {
            num: "01",
            title: "Tag the slow-moving products",
            body: "In Pabloo, you choose which products or collections trigger a bonus store credit reward. You can set a flat dollar amount or a percentage of the purchase value.",
        },
        {
            num: "02",
            title: "Customer buys at full price",
            body: "The customer pays the normal price. No public discount code, no markdown visible on the product page. The product's listed value stays intact.",
        },
        {
            num: "03",
            title: "Pabloo issues the credit automatically",
            body: "After the order is confirmed, Pabloo credits the customer's account. They get a notification that credit is waiting for them on their next order.",
        },
        {
            num: "04",
            title: "Customer returns to spend it",
            body: "The credit brings them back. They come in to use it and usually spend more than the credit amount. You moved old stock and acquired a repeat purchase in one transaction.",
        },
    ];
    return (
        <section style={{ background: "#050505", padding: "80px 24px" }}>
            <div style={{ maxWidth: "860px", margin: "0 auto" }}>
                <div style={{ textAlign: "center", marginBottom: "56px" }}>
                    <p style={{ fontSize: "11px", fontWeight: 700, color: "#22c55e", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: "10px" }}>
                        How It Works
                    </p>
                    <h2 style={{ fontSize: "clamp(24px, 4vw, 36px)", fontWeight: 800, color: "#fff", lineHeight: 1.2 }}>
                        Liquidate stock in four steps
                    </h2>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    {steps.map((step) => (
                        <div key={step.num} style={{ display: "flex", gap: "24px", alignItems: "flex-start", background: "#111", border: "1px solid #1e1e1e", borderRadius: "14px", padding: "24px 28px" }}>
                            <div style={{ fontSize: "22px", fontWeight: 800, color: "#22c55e", minWidth: "36px", lineHeight: 1 }}>{step.num}</div>
                            <div>
                                <h3 style={{ fontSize: "15px", fontWeight: 700, color: "#fff", marginBottom: "6px" }}>{step.title}</h3>
                                <p style={{ fontSize: "13.5px", color: "#9ca3af", lineHeight: 1.6, margin: 0 }}>{step.body}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function ComparisonSection() {
    const rows = [
        { label: "Product sells at full listed price", credit: true, markdown: false, liquidator: false },
        { label: "Customer returns for a second order", credit: true, markdown: false, liquidator: false },
        { label: "Brand pricing signal stays intact", credit: true, markdown: false, liquidator: false },
        { label: "Revenue stays inside your store", credit: true, markdown: true, liquidator: false },
        { label: "No discount conditioning for future orders", credit: true, markdown: false, liquidator: false },
        { label: "Works without a public sale page", credit: true, markdown: false, liquidator: false },
    ];
    return (
        <section style={{ background: "#0a0a0a", padding: "80px 24px" }}>
            <div style={{ maxWidth: "860px", margin: "0 auto" }}>
                <div style={{ textAlign: "center", marginBottom: "48px" }}>
                    <p style={{ fontSize: "11px", fontWeight: 700, color: "#22c55e", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: "10px" }}>
                        How the Options Compare
                    </p>
                    <h2 style={{ fontSize: "clamp(24px, 4vw, 36px)", fontWeight: 800, color: "#fff", lineHeight: 1.2 }}>
                        Store credit vs the alternatives
                    </h2>
                </div>
                <div style={{ background: "#111", border: "1px solid #1e1e1e", borderRadius: "16px", overflow: "hidden" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 140px 140px 140px", borderBottom: "1px solid #1e1e1e", padding: "14px 24px" }}>
                        <div />
                        <div style={{ fontSize: "12px", fontWeight: 700, color: "#22c55e", textAlign: "center" }}>Store credit bonus</div>
                        <div style={{ fontSize: "12px", fontWeight: 700, color: "#9ca3af", textAlign: "center" }}>Markdown sale</div>
                        <div style={{ fontSize: "12px", fontWeight: 700, color: "#9ca3af", textAlign: "center" }}>3rd-party liquidator</div>
                    </div>
                    {rows.map((row, i) => (
                        <div
                            key={row.label}
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 140px 140px 140px",
                                padding: "14px 24px",
                                borderBottom: i < rows.length - 1 ? "1px solid #1a1a1a" : "none",
                                alignItems: "center",
                            }}
                        >
                            <div style={{ fontSize: "13.5px", color: "#d1d5db" }}>{row.label}</div>
                            <div style={{ display: "flex", justifyContent: "center" }}>{row.credit ? <CheckIcon /> : <XIcon />}</div>
                            <div style={{ display: "flex", justifyContent: "center" }}>{row.markdown ? <CheckIcon /> : <XIcon />}</div>
                            <div style={{ display: "flex", justifyContent: "center" }}>{row.liquidator ? <CheckIcon /> : <XIcon />}</div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function FeaturesSection() {
    const features = [
        {
            icon: <RefreshIcon />,
            title: "Per-product credit rules",
            body: "Set different credit amounts for different products or collections. Move specific SKUs without running a sitewide sale.",
        },
        {
            icon: <CreditIcon />,
            title: "Credit applied automatically at checkout",
            body: "Customers don't need a code. Their balance appears at checkout and applies to their next order without friction.",
        },
        {
            icon: <TrendIcon />,
            title: "Track which products drove returns",
            body: "Pabloo logs every credit event. You can see which liquidation campaigns generated follow-on orders and how much those orders were worth.",
        },
        {
            icon: <BoxIcon />,
            title: "Works alongside existing promotions",
            body: "Store credit bonuses run independently of your Shopify discount codes. You can use both without them interfering with each other.",
        },
    ];
    return (
        <section style={{ background: "#050505", padding: "80px 24px" }}>
            <div style={{ maxWidth: "900px", margin: "0 auto" }}>
                <div style={{ textAlign: "center", marginBottom: "56px" }}>
                    <p style={{ fontSize: "11px", fontWeight: 700, color: "#22c55e", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: "10px" }}>
                        Built For This
                    </p>
                    <h2 style={{ fontSize: "clamp(24px, 4vw, 36px)", fontWeight: 800, color: "#fff", lineHeight: 1.2 }}>
                        Tools to run a smarter liquidation campaign
                    </h2>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "20px" }}>
                    {features.map((f) => (
                        <div key={f.title} style={{ background: "#111", border: "1px solid #1e1e1e", borderRadius: "16px", padding: "28px" }}>
                            <div style={{ marginBottom: "16px" }}>{f.icon}</div>
                            <h3 style={{ fontSize: "15px", fontWeight: 700, color: "#fff", marginBottom: "8px" }}>{f.title}</h3>
                            <p style={{ fontSize: "13.5px", color: "#9ca3af", lineHeight: 1.65, margin: 0 }}>{f.body}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function UseCasesSection() {
    const cases = [
        {
            title: "End-of-season apparel",
            body: "Issue 15% back in store credit on last season's styles. Move the stock before it needs to be stored another year, and pull customers back in when the new collection drops.",
        },
        {
            title: "Slow-moving SKUs",
            body: "Attach a bonus credit offer to specific product variants that haven't moved in 60+ days. No sitewide sale needed, no brand signal damaged.",
        },
        {
            title: "Overstock after a failed campaign",
            body: "If a product launch underperformed, offer a credit bonus to get existing customers to try it. Better than a markdown that announces to the market that the launch failed.",
        },
        {
            title: "Expiring or perishable goods",
            body: "For products with shelf lives, attach a credit bonus to drive fast purchases. Customers get rewarded for buying now, and you avoid writing off expired inventory.",
        },
    ];
    return (
        <section style={{ background: "#0a0a0a", padding: "80px 24px" }}>
            <div style={{ maxWidth: "860px", margin: "0 auto" }}>
                <div style={{ textAlign: "center", marginBottom: "48px" }}>
                    <p style={{ fontSize: "11px", fontWeight: 700, color: "#22c55e", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: "10px" }}>
                        Use Cases
                    </p>
                    <h2 style={{ fontSize: "clamp(24px, 4vw, 36px)", fontWeight: 800, color: "#fff", lineHeight: 1.2 }}>
                        Where this works in practice
                    </h2>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "16px" }}>
                    {cases.map((c) => (
                        <div key={c.title} style={{ background: "#111", border: "1px solid #1e1e1e", borderRadius: "14px", padding: "24px 28px" }}>
                            <h3 style={{ fontSize: "15px", fontWeight: 700, color: "#fff", marginBottom: "10px" }}>{c.title}</h3>
                            <p style={{ fontSize: "13.5px", color: "#9ca3af", lineHeight: 1.65, margin: 0 }}>{c.body}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function FinalCTA({ onCTA }) {
    return (
        <section style={{ background: "#0a0a0a", borderTop: "1px solid #1a1a1a", padding: "80px 24px", textAlign: "center" }}>
            <div style={{ maxWidth: "560px", margin: "0 auto" }}>
                <h2 style={{ fontSize: "clamp(24px, 4vw, 36px)", fontWeight: 800, color: "#fff", lineHeight: 1.2, marginBottom: "16px" }}>
                    Clear your old stock without a clearance sale
                </h2>
                <p style={{ fontSize: "15px", color: "#9ca3af", lineHeight: 1.6, marginBottom: "32px" }}>
                    14-day free trial. Works with your existing Shopify store. Set up a credit campaign in minutes.
                </p>
                <button
                    onClick={onCTA}
                    style={{ padding: "15px 32px", background: "#22c55e", color: "#fff", border: "none", borderRadius: "10px", fontSize: "15px", fontWeight: 700, cursor: "pointer" }}
                >
                    Start free trial
                </button>
            </div>
        </section>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function InventoryLiquidation() {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "auto" });
        sendSlackMessageTracked("Lead is visiting the Inventory Liquidation page.", "#website-updates");
    }, []);

    const handleCTA = () => { window.location.href = "https://app.pabloo.com/sign-up"; };

    return (
        <div style={{ background: "#050505" }}>
            <NavBarComponent />
            <Hero onCTA={handleCTA} />
            <StatsStrip />
            <ProblemSection />
            <HowItWorksSection />
            <ComparisonSection />
            <FeaturesSection />
            <UseCasesSection />
            <ModernPricingView />
            <FinalCTA onCTA={handleCTA} />
            <FooterComponent />
        </div>
    );
}
